export type CurrencyCode =
  | "USD"
  | "INR"
  | "AED"
  | "EUR"
  | "GBP"
  | "SAR"
  | "QAR"
  | "KWD"
  | "BHD"
  | "OMR"
  | "SGD"
  | "AUD"
  | "CAD"
  | "JPY"
  | "CNY";

export interface Currency {
  code: CurrencyCode;
  name: string;
  symbol: string;
  locale: string;
  decimalPlaces: number;
}

export const DEFAULT_CURRENCY: CurrencyCode = "USD";

export const CURRENCIES: Record<CurrencyCode, Currency> = {
  USD: {
    code: "USD",
    name: "US Dollar",
    symbol: "$",
    locale: "en-US",
    decimalPlaces: 2,
  },
  INR: {
    code: "INR",
    name: "Indian Rupee",
    symbol: "₹",
    locale: "en-IN",
    decimalPlaces: 2,
  },
  AED: {
    code: "AED",
    name: "UAE Dirham",
    symbol: "د.إ",
    locale: "en-AE",
    decimalPlaces: 2,
  },
  EUR: {
    code: "EUR",
    name: "Euro",
    symbol: "€",
    locale: "de-DE",
    decimalPlaces: 2,
  },
  GBP: {
    code: "GBP",
    name: "British Pound",
    symbol: "£",
    locale: "en-GB",
    decimalPlaces: 2,
  },
  SAR: {
    code: "SAR",
    name: "Saudi Riyal",
    symbol: "ر.س",
    locale: "ar-SA",
    decimalPlaces: 2,
  },
  QAR: {
    code: "QAR",
    name: "Qatari Riyal",
    symbol: "ر.ق",
    locale: "ar-QA",
    decimalPlaces: 2,
  },
  KWD: {
    code: "KWD",
    name: "Kuwaiti Dinar",
    symbol: "د.ك",
    locale: "ar-KW",
    decimalPlaces: 3,
  },
  BHD: {
    code: "BHD",
    name: "Bahraini Dinar",
    symbol: ".د.ب",
    locale: "ar-BH",
    decimalPlaces: 3,
  },
  OMR: {
    code: "OMR",
    name: "Omani Rial",
    symbol: "ر.ع.",
    locale: "ar-OM",
    decimalPlaces: 3,
  },
  SGD: {
    code: "SGD",
    name: "Singapore Dollar",
    symbol: "S$",
    locale: "en-SG",
    decimalPlaces: 2,
  },
  AUD: {
    code: "AUD",
    name: "Australian Dollar",
    symbol: "A$",
    locale: "en-AU",
    decimalPlaces: 2,
  },
  CAD: {
    code: "CAD",
    name: "Canadian Dollar",
    symbol: "C$",
    locale: "en-CA",
    decimalPlaces: 2,
  },
  JPY: {
    code: "JPY",
    name: "Japanese Yen",
    symbol: "¥",
    locale: "ja-JP",
    decimalPlaces: 0,
  },
  CNY: {
    code: "CNY",
    name: "Chinese Yuan",
    symbol: "¥",
    locale: "zh-CN",
    decimalPlaces: 2,
  },
};

export const CURRENCY_LIST: Currency[] = Object.values(CURRENCIES).sort(
  (a, b) => a.name.localeCompare(b.name)
);

/**
 * Formats a number into a currency string based on the currency's locale and settings.
 * Handles null/undefined/NaN by defaulting to 0.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currencyCode: CurrencyCode = DEFAULT_CURRENCY,
  options: { compact?: boolean; showCode?: boolean } = {}
): string {
  const safeAmount =
    amount === null || amount === undefined || isNaN(amount) ? 0 : amount;

  const currency = CURRENCIES[currencyCode] || CURRENCIES[DEFAULT_CURRENCY];
  const { locale, decimalPlaces } = currency;

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: options.compact ? 0 : decimalPlaces,
      maximumFractionDigits: options.compact ? 1 : decimalPlaces,
      notation: options.compact ? "compact" : "standard",
    });

    let formatted = formatter.format(safeAmount);

    if (options.showCode) {
      // Some locales might already include the code or symbol in a way that makes appending redundant,
      // but usually 'showCode' implies an explicit suffix like "USD" if the symbol is ambiguous ($).
      // However, Intl with style: 'currency' usually puts the symbol.
      // If we want the ISO code explicitly:
      formatted = `${formatted} ${currencyCode}`;
    }

    return formatted;
  } catch (error) {
    console.error(`Error formatting currency ${currencyCode}:`, error);
    // Fallback
    return `${currency.symbol}${safeAmount.toFixed(decimalPlaces)}`;
  }
}

/**
 * Formats a number according to the currency's locale and settings, but without the currency symbol.
 * Handles null/undefined/NaN by defaulting to 0.
 */
export function formatCurrencyNumber(
  amount: number | null | undefined,
  currencyCode: CurrencyCode = DEFAULT_CURRENCY,
  options: { compact?: boolean; showCode?: boolean } = {}
): string {
  const safeAmount =
    amount === null || amount === undefined || isNaN(amount) ? 0 : amount;

  const currency = CURRENCIES[currencyCode] || CURRENCIES[DEFAULT_CURRENCY];
  const { locale, decimalPlaces } = currency;

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: options.compact ? 0 : decimalPlaces,
      maximumFractionDigits: options.compact ? 1 : decimalPlaces,
      notation: options.compact ? "compact" : "standard",
    });

    let formatted = formatter.format(safeAmount);

    if (options.showCode) {
      formatted = `${formatted} ${currencyCode}`;
    }

    return formatted;
  } catch (error) {
    console.error(`Error formatting currency number ${currencyCode}:`, error);
    // Fallback
    return `${safeAmount.toFixed(decimalPlaces)}`;
  }
}

/**
 * Returns the symbol for a given currency code.
 */
export function getCurrencySymbol(
  currencyCode: CurrencyCode = DEFAULT_CURRENCY
): string {
  return (
    CURRENCIES[currencyCode]?.symbol || CURRENCIES[DEFAULT_CURRENCY].symbol
  );
}

/**
 * Parses a string input (e.g. from a form field) into a number.
 * It attempts to handle locale-specific decimal separators by detecting the separator
 * from the currency's locale.
 *
 * Example:
 * - "1,234.56" (USD) -> 1234.56
 * - "1.234,56" (EUR/DE) -> 1234.56
 */
export function parseCurrencyAmount(
  value: string,
  currencyCode: CurrencyCode = DEFAULT_CURRENCY
): number {
  if (!value) return 0;

  const currency = CURRENCIES[currencyCode] || CURRENCIES[DEFAULT_CURRENCY];

  try {
    // 1. Determine the decimal separator for this locale
    // We format 1.1 to see what the separator is (e.g. "." or ",")
    const parts = new Intl.NumberFormat(currency.locale).formatToParts(1.1);
    const decimalPart = parts.find((p) => p.type === "decimal");
    const decimalSeparator = decimalPart ? decimalPart.value : ".";

    // 2. Create a regex to keep only digits, minus sign, and the decimal separator
    // We escape the separator because it might be a dot
    const escapedSeparator = decimalSeparator.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    // Match anything that is NOT a digit, minus, or the specific decimal separator
    const cleanRegex = new RegExp(`[^0-9-${escapedSeparator}]`, "g");

    // 3. Remove all grouping separators and symbols
    let cleaned = value.replace(cleanRegex, "");

    // 4. Normalize decimal separator to dot for JS parseFloat
    cleaned = cleaned.replace(decimalSeparator, ".");

    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    console.error(`Error parsing currency amount for ${currencyCode}:`, error);
    return 0;
  }
}